<template>
  <div class="photo-booth">
    <!-- Hidden video element -->
    <video
      ref="videoEl"
      class="hidden-video"
      playsinline
      muted
      autoplay
      webkit-playsinline
    />

    <!-- Main canvas -->
    <div ref="canvasWrapperEl" class="canvas-wrapper" :class="{ transitioning }">
      <canvas ref="canvasEl" />
      <ScreenEditor
        v-if="debugMode"
        :canvasEl="canvasEl"
        :initialScreen="currentOverlay?.screen"
        :initialCorners="currentOverlay?.corners"
        @update="onScreenUpdate"
      />
      <CountdownOverlay :active="countingDown" :from="timer" @done="doCapture" />
      <CaptureFlash :flash="flashing" />
      <transition name="instructions-fade">
        <div v-if="showInstructions" class="instructions-overlay">
          <div v-if="isMobile" class="instructions-content">
            <p>Swipe <kbd>&larr;</kbd> <kbd>&rarr;</kbd> Switch Layer</p>
            <p>Swipe <kbd>&uarr;</kbd> <kbd>&darr;</kbd> Switch Filter</p>
          </div>
          <div v-else class="instructions-content">
            <p><kbd>Space</kbd> Take Photo</p>
            <p><kbd>&larr;</kbd> <kbd>&rarr;</kbd> Switch Layer</p>
            <p><kbd>&uarr;</kbd> <kbd>&darr;</kbd> Switch Filter</p>
          </div>
        </div>
      </transition>
    </div>

    <!-- Filter indicator -->
    <div class="filter-indicator-wrapper">
      <button v-if="filterIndex !== 0" class="reset-filter-btn" @click="resetFilter">
        {{ currentFilter().name }} &times;
      </button>
    </div>

    <!-- Controls -->
    <div class="controls">
      <button class="capture-btn" @click="startCapture" :disabled="countingDown" title="Capture (Space)">
        <div class="capture-btn-inner"></div>
      </button>
    </div>

    <!-- Timer selector -->
    <div class="timer-selector">
      <button
        v-for="t in timerOptions"
        :key="t"
        class="timer-btn"
        :class="{ active: timer === t }"
        @click="timer = t"
      >{{ t === 0 ? 'Off' : t + 's' }}</button>
    </div>

    <!-- Error -->
    <div v-if="error" class="error-message">{{ error }}</div>

    <!-- Ad overlay -->
    <AdOverlay
      :visible="showAdOverlay"
      :photoUrl="pendingPhoto"
      @done="onAdDone"
      @dismiss="onAdDismiss"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useCamera } from '@/composables/useCamera'
import { useOverlays } from '@/composables/useOverlays'
import { useCapture } from '@/composables/useCapture'
import { useKeyboard } from '@/composables/useKeyboard'
import { filterConfigs } from '@/config/filters'
import CountdownOverlay from './CountdownOverlay.vue'
import CaptureFlash from './CaptureFlash.vue'
import ScreenEditor from './ScreenEditor.vue'
import AdOverlay from './AdOverlay.vue'

const canvasEl = ref(null)
const canvasWrapperEl = ref(null)
const countingDown = ref(false)
const flashing = ref(false)
const debugMode = ref(false)
const screenOverride = ref(null)
const cornersOverride = ref(null)
const showInstructions = ref(true)
const timerOptions = [0, 3, 5, 10]
const timer = ref(3)
const filterIndex = ref(0)
const currentFilter = () => filterConfigs[filterIndex.value]

const showFilterName = ref(false)
let filterTimeout = null

function showFilterLabel() {
  showFilterName.value = true
  clearTimeout(filterTimeout)
  filterTimeout = setTimeout(() => { showFilterName.value = false }, 1200)
}

function resetFilter() {
  filterIndex.value = 0
}

function nextFilter() {
  filterIndex.value = (filterIndex.value + 1) % filterConfigs.length
  showFilterLabel()
}
function prevFilter() {
  filterIndex.value = (filterIndex.value - 1 + filterConfigs.length) % filterConfigs.length
  showFilterLabel()
}
const isMobile = ref(window.matchMedia('(max-width: 600px)').matches || 'ontouchstart' in window)
let animFrameId = null

const pendingPhoto = ref(null)
const showAdOverlay = ref(false)

const { videoRef: videoEl, streamActive, error, startCamera } = useCamera()
const { currentOverlay, overlayImage, transitioning, preloadAll, nextOverlay, prevOverlay } = useOverlays()
const { compositeFrame, capturePhoto, downloadPhoto } = useCapture()

function onScreenUpdate({ corners, screen }) {
  screenOverride.value = { ...screen }
  cornersOverride.value = { ...corners }
  console.log('🎯 Active screen rect:', JSON.stringify(screen))
  console.log('🔲 Corners:', JSON.stringify(corners))
}

function startCapture() {
  if (!streamActive.value) return
  if (countingDown.value) {
    countingDown.value = false
    return
  }
  if (timer.value === 0) {
    doCapture()
  } else {
    countingDown.value = true
  }
}

// Set to true to enable ads before download
const ADS_ENABLED = false

function doCapture() {
  countingDown.value = false
  flashing.value = true
  const dataUrl = capturePhoto(canvasEl.value)
  if (ADS_ENABLED) {
    pendingPhoto.value = dataUrl
    setTimeout(() => {
      flashing.value = false
      showAdOverlay.value = true
    }, 350)
  } else {
    downloadPhoto(dataUrl)
    setTimeout(() => {
      flashing.value = false
    }, 350)
  }
}

function onAdDone() {
  downloadPhoto(pendingPhoto.value)
  showAdOverlay.value = false
  pendingPhoto.value = null
}

function onAdDismiss() {
  showAdOverlay.value = false
  pendingPhoto.value = null
}

useKeyboard({
  onCapture: startCapture,
  onNext: nextOverlay,
  onPrev: prevOverlay,
  onFilterNext: nextFilter,
  onFilterPrev: prevFilter,
  canvasWrapper: canvasWrapperEl
})

function renderLoop() {
  const canvas = canvasEl.value
  const video = videoEl.value
  const overlay = overlayImage.value
  const config = currentOverlay.value

  if (canvas && overlay && config) {
    const ctx = canvas.getContext('2d')
    if (canvas.width !== overlay.naturalWidth || canvas.height !== overlay.naturalHeight) {
      canvas.width = overlay.naturalWidth
      canvas.height = overlay.naturalHeight
    }
    const activeConfig = cornersOverride.value
      ? { ...config, corners: cornersOverride.value, screen: screenOverride.value }
      : config
    const f = currentFilter()
    compositeFrame(ctx, video, overlay, activeConfig, canvas.width, canvas.height, streamActive.value, f.filter, f.effect)
  }

  animFrameId = requestAnimationFrame(renderLoop)
}

onMounted(async () => {
  await preloadAll()
  await startCamera()
  renderLoop()
  setTimeout(() => {
    showInstructions.value = false
  }, 3000)
})

onUnmounted(() => {
  if (animFrameId) {
    cancelAnimationFrame(animFrameId)
  }
})
</script>

<style scoped>
.photo-booth {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 0;
  width: 100%;
  max-height: 100dvh;
}

.hidden-video {
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
}

.canvas-wrapper {
  position: relative;
  max-width: 100%;
  max-height: calc(100vh - 240px);
  transition: opacity 0.15s ease;
}

.canvas-wrapper.transitioning {
  opacity: 0.3;
}

.canvas-wrapper canvas {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 240px);
  object-fit: contain;
  border-radius: 8px;
}

.instructions-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 5;
  pointer-events: none;
  border-radius: 8px;
}

.instructions-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.instructions-content p {
  font-size: 18px;
  color: white;
  font-weight: 500;
}

.instructions-content kbd {
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  padding: 2px 8px;
  font-family: inherit;
  font-size: 16px;
  min-width: 28px;
}

.instructions-fade-enter-active {
  transition: opacity 0.3s ease;
}
.instructions-fade-leave-active {
  transition: opacity 1s ease;
}
.instructions-fade-enter-from,
.instructions-fade-leave-to {
  opacity: 0;
}

.controls {
  display: flex;
  align-items: center;
  gap: 24px;
}

.capture-btn {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: 4px solid white;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  transition: all 0.2s;
}

.capture-btn:hover:not(:disabled) {
  transform: scale(1.05);
}

.capture-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.capture-btn-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: #ff3b30;
  transition: background 0.2s;
}

.capture-btn:hover:not(:disabled) .capture-btn-inner {
  background: #ff6259;
}

.filter-indicator-wrapper {
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.reset-filter-btn {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 3px 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.reset-filter-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.filter-indicator {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.1);
  padding: 4px 14px;
  border-radius: 12px;
  letter-spacing: 0.5px;
}

.timer-selector {
  display: flex;
  gap: 8px;
}

.timer-btn {
  padding: 4px 12px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}

.timer-btn.active {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border-color: rgba(255, 255, 255, 0.4);
}

.timer-btn:hover {
  border-color: rgba(255, 255, 255, 0.4);
}

.error-message {
  color: #ff3b30;
  font-size: 14px;
  padding: 12px 20px;
  background: rgba(255, 59, 48, 0.1);
  border-radius: 8px;
}

@media (max-width: 600px) {
  .photo-booth {
    gap: 8px;
  }

  .canvas-wrapper,
  .canvas-wrapper canvas {
    max-height: calc(100dvh - 120px);
    border-radius: 4px;
  }

  .capture-btn {
    width: 60px;
    height: 60px;
    border-width: 3px;
  }

  .instructions-content p {
    font-size: 14px;
  }

  .instructions-content kbd {
    font-size: 13px;
    padding: 1px 6px;
  }

  .error-message {
    font-size: 12px;
    padding: 8px 12px;
  }
}

</style>
